import { gql, useApolloClient } from "@apollo/client";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuid4 } from "uuid";
import { OpusAgentChatTurnStatus, OpusAgentModel } from "@src/types.generated";
import {
  OpusAgentChatDocument,
  OpusAgentChatModelSeedFragment,
  OpusAgentChatModelSeedFragmentDoc,
  OpusAgentChatQuery,
  OpusAgentChatQueryVariables,
  OpusAgentEventFragment,
  OpusAgentMostRecentChatDocument,
  OpusAgentMostRecentChatQuery,
  OpusAgentTurnEventsDocument,
  OpusAgentTurnEventsQuery,
  OpusAgentTurnEventsQueryVariables,
  OpusAgentTurnFragment,
  OpusAgentTurnsDocument,
  OpusAgentTurnsQuery,
  OpusAgentTurnsQueryVariables,
  useAbortOpusAgentTurnMutation,
  useEditOpusAgentMessageMutation,
  useOpusAgentChatQuery,
  useOpusAgentDefaultModelQuery,
  useSendOpusAgentMessageMutation,
} from "./useOpusAgentChat.generated";
import { getEventKind, highestEventIndex, mergeEvents } from "./events";
import { getLiveContext } from "./liveContext";
import {
  ChatModelState,
  NEW_CHAT_KEY,
  chatKeyFor,
  chatResolved,
  effectiveChatModel,
  hydrated as hydratedModelTransition,
  initialChatModelState,
  selectModel as selectModelTransition,
  sendSubmitted,
  sendSucceeded,
} from "./chatModelState";
import { isOpusAgentModelAvailable } from "./models";
import {
  readPersistedSelectedChat,
  useOpusAgentDrawerStore,
} from "./useOpusAgentDrawerStore";
import { useOpusAgentModelPreferenceStore } from "./useOpusAgentModelPreferenceStore";

const POLL_INTERVAL_MS = 1000;
const MAX_CONSECUTIVE_POLL_FAILURES = 5;
// How long one poll batch may stay unsettled before it is treated as hung:
// counted as a failure for its turns and superseded by a fresh batch.
const POLL_HANG_TIMEOUT_MS = 8000;
// Waits between a settle's events re-fetches when the fetch yields nothing
// new while the transcript still lacks its closing event (the status can be
// read before the server's finalize flush, and so can the events). Five
// attempts in ~3.5s: well under POLL_HANG_TIMEOUT_MS, so a waiting settle is
// never superseded as hung.
const SETTLE_REFETCH_DELAYS_MS = [500, 1000, 1000, 1000];

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

// Whether a transcript already reads as finished for this terminal status:
// a SUCCEEDED turn ends in assistant text, a FAILED one in an error (or
// text); an ABORTED turn is user-initiated, nothing to wait for. The
// closing event often lands through the regular events poll BEFORE the
// status poll reports the transition, so an empty settle fetch is healthy
// then — it is only suspicious when the transcript still ends mid-work.
const transcriptLooksClosed = (
  events: OpusAgentEventFragment[],
  status: OpusAgentChatTurnStatus,
): boolean => {
  if (status === OpusAgentChatTurnStatus.Aborted) return true;
  const last = events[events.length - 1];
  if (!last) return false;
  const kind = getEventKind(last.payload);
  if (status === OpusAgentChatTurnStatus.Succeeded)
    return kind === "assistantText";
  return kind === "error" || kind === "assistantText";
};
// Page size for OpusAgentTurnEvents reads. The query is a PaginatedList; an
// unpaginated read gets the server's default page, which silently truncated
// long turns (a real one had 64 events) while the turn still rendered as
// finished. Every read passes this limit and pages to a short page before
// anything is applied.
export const EVENTS_PAGE_SIZE = 100;
// Floor for the chat-scoped status poll's page. The server holds at most one
// RUNNING turn per chat (DB-enforced), but this hook tracks MORE than that:
// every local RUNNING mirror awaiting its supersession — a burst of sends
// leaves each earlier turn RUNNING locally until the poll reports it
// ABORTED. The filter input has no id/status field, so the page (newest
// first) is sized from local state to reach the oldest tracked turn; this
// constant is only the floor.
export const STATUS_POLL_LIMIT = 5;
// A tracked RUNNING turn absent from this many consecutive status responses
// is settled terminal-by-absence instead of being polled forever: either it
// was deleted server-side, or newer rows pushed it out of the page — and
// with one RUNNING per chat, a turn with newer rows above it is terminal
// there too.
export const MAX_STATUS_MISSES = 3;

export type OpusAgentTurnState = {
  id: string;
  userMessage: string;
  status: OpusAgentChatTurnStatus;
  createdAt: string;
  completedAt?: string | null;
  supersededByEditAt?: string | null;
  events: OpusAgentEventFragment[];
  citations: OpusAgentTurnFragment["citations"];
  // Local-only: optimistic turn whose send mutation hasn't resolved yet.
  pending?: boolean;
  // Local-only: the send mutation itself failed (network / backend error).
  sendFailed?: boolean;
  pollFailed?: boolean;
};

export interface UseOpusAgentChatReturn {
  turns: OpusAgentTurnState[];
  loading: boolean;
  loadFailed: boolean;
  isRunning: boolean;
  // The selected chat; null while composing a chat that doesn't exist yet.
  activeChatId: string | null;
  // The model the NEXT turn runs on (pending pick, else the chat's stored
  // selection, else the server default); null until the default is known.
  effectiveModel: OpusAgentModel | null;
  defaultModel: OpusAgentModel | null;
  sendMessage: (message: string) => void;
  abort: () => void;
  resumePolling: (turnId: string) => void;
  retryLoad: () => void;
  selectChat: (chatId: string) => void;
  startNewChat: () => void;
  // Resolves false when the edit was rejected (the caller keeps its editor
  // open); true when the chat was rewound and the new turn appended.
  editMessage: (turnId: string, newMessage: string) => Promise<boolean>;
  // Pick the model for this chat's next turn (allowed any time, mid-run too).
  selectModel: (model: OpusAgentModel) => void;
}

const isPollable = (turn: OpusAgentTurnState): boolean =>
  turn.status === OpusAgentChatTurnStatus.Running &&
  !turn.pollFailed &&
  !turn.pending;

const newestRunningTurn = (
  list: OpusAgentTurnState[],
): OpusAgentTurnState | undefined =>
  [...list]
    .reverse()
    .find(
      (turn) =>
        turn.status === OpusAgentChatTurnStatus.Running && !turn.pollFailed,
    );

const toTurnState = (turn: OpusAgentTurnFragment): OpusAgentTurnState => ({
  id: turn.id,
  userMessage: turn.userMessage,
  status: turn.status,
  createdAt: turn.createdAt,
  completedAt: turn.completedAt,
  supersededByEditAt: turn.supersededByEditAt,
  events: mergeEvents(turn.events, []),
  citations: turn.citations,
});

type TurnStatusFields = Pick<OpusAgentTurnState, "status" | "completedAt">;

// Terminal-wins merge for a turn status arriving from any server read
// (hydration, status poll, settle, abort response): a locally terminal turn
// (SUCCEEDED / FAILED / ABORTED) is final — it is never dragged back to
// RUNNING by a read that raced its settlement, nor by a late or failed
// abort. Only a locally RUNNING turn takes the incoming status.
export const mergeTurnStatus = (
  local: TurnStatusFields,
  incoming: TurnStatusFields,
): TurnStatusFields =>
  local.status === OpusAgentChatTurnStatus.Running
    ? { status: incoming.status, completedAt: incoming.completedAt }
    : { status: local.status, completedAt: local.completedAt };

// The one place that decides what a server row (hydration, re-hydration, a
// send result landing on its optimistic row) may overwrite on a local row:
// ids, createdAt, message and citations come from the server; status follows
// terminal-wins; the supersede stamp is one-way (a local rewind mirror is
// never undone by a row the server produced before that rewind); events are
// index-merged; local-only poll state is kept.
export const mergeTurnFromServer = (
  local: OpusAgentTurnState,
  incoming: OpusAgentTurnFragment,
): OpusAgentTurnState => ({
  ...toTurnState(incoming),
  ...mergeTurnStatus(local, incoming),
  // Only a terminal server read carries the final curated set; a RUNNING
  // snapshot (a send result is the turn as of send time) must not wipe the
  // cards a settle already landed.
  citations:
    incoming.status === OpusAgentChatTurnStatus.Running
      ? local.citations
      : incoming.citations,
  supersededByEditAt: local.supersededByEditAt ?? incoming.supersededByEditAt,
  events: mergeEvents(local.events, incoming.events),
  pollFailed: local.pollFailed,
});

type PolledStatus = {
  id: string;
  status: OpusAgentChatTurnStatus;
  completedAt?: string | null;
  supersededByEditAt?: string | null;
  citations: OpusAgentTurnFragment["citations"];
};

// State model for the Opus Agent chat drawer, multi-chat edition. On first
// activation the most recent chat (by lastActivity) is selected via
// OpusAgentChats and hydrated via OpusAgentChat(id); selecting another chat
// re-hydrates; "new chat" clears to an empty transcript whose first send goes
// chatId=null and adopts the created chat's id. While a turn is RUNNING the
// hook polls the latest RUNNING turn every second for events strictly after
// the highest index seen so far, plus one chat-scoped status fetch per batch.
export const useOpusAgentChat = ({
  active,
  userId = null,
}: {
  active: boolean;
  // The signed-in user, for the persisted selected chat (agent surface
  // only). Without one nothing is restored or written.
  userId?: string | null;
}): UseOpusAgentChatReturn => {
  const router = useRouter();
  const client = useApolloClient();
  const [turns, setTurns] = useState<OpusAgentTurnState[]>([]);
  // Two different gates. `activated` latches: the drawer has been used at
  // least once, so the initial most-recent-chat lookup may run (and, once
  // resolved, never repeats). `active` is continuous: whether the hook may
  // talk to the backend RIGHT NOW — lookup, hydration and the 1s poll all
  // pause in place while it is false (a kill switch landing mid-session) and
  // resume through the normal paths when it flips back.
  const [activated, setActivated] = useState(false);
  // `active` as a ref, for async continuations that must not act after a
  // deactivation landed while their request was in flight.
  const activeRef = useRef(active);
  activeRef.current = active;
  const [hydrated, setHydrated] = useState(false);
  // undefined = the initial most-recent-chat lookup hasn't resolved yet;
  // null = composing a new chat that doesn't exist server-side yet.
  const [selectedChatId, setSelectedChatId] = useState<
    string | null | undefined
  >(undefined);
  const [selectionFailed, setSelectionFailed] = useState(false);
  const [selectionAttempt, setSelectionAttempt] = useState(0);
  // All model bookkeeping lives in chatModelState.ts as one structure with
  // pure transitions; the hook holds the instance (ref for synchronous reads
  // in async continuations, state for rendering) and mirrors stored writes
  // into the Apollo cache so the switch-time reseed agrees with the server.
  const modelStateRef = useRef(initialChatModelState());
  const [modelState, setModelState] = useState(modelStateRef.current);
  const commitModelState = useCallback((next: ChatModelState) => {
    modelStateRef.current = next;
    setModelState(next);
  }, []);
  // The model-state version a hydration fetch started under (see hydrated()).
  const fetchModelVersionRef = useRef(0);
  const hydratedRef = useRef(false);
  const selectedChatIdRef = useRef<string | null | undefined>(undefined);
  const turnsRef = useRef(turns);
  turnsRef.current = turns;
  // Bumped on every chat switch: an in-flight send/edit result whose session
  // is stale belongs to a chat the user has left and must not touch state.
  const sessionRef = useRef(0);
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  // The persisted selection this activation restored instead of running the
  // most-recent-chat lookup; cleared once it hydrates. If it turns out to
  // be gone or forbidden, the lookup runs after all.
  const restoredChatIdRef = useRef<string | null>(null);
  // Persist the current selection for this user (null = a new composition,
  // so a mid-composition reload lands on the most recent chat, not on a
  // phantom). Nothing is written while inactive (kill switch).
  const persistSelection = useCallback((chatId: string | null) => {
    const currentUserId = userIdRef.current;
    if (!activeRef.current || typeof currentUserId !== "string") return;
    useOpusAgentDrawerStore.getState().setSelectedChat(chatId, currentUserId);
  }, []);

  useEffect(() => {
    if (active) setActivated(true);
  }, [active]);

  // Deactivation abandons whatever is in flight: dropping the settle
  // entries makes every pending settle stale under the generation guard (a
  // late response is ignored), and the held terminals go with them — the
  // status poll re-detects the transition on resume.
  useEffect(() => {
    if (active) return;
    settlingRef.current.clear();
    pendingTerminalRef.current.clear();
  }, [active]);

  const [sendOpusAgentMessage] = useSendOpusAgentMessageMutation();
  const [abortOpusAgentTurn] = useAbortOpusAgentTurnMutation();
  const [editOpusAgentMessage] = useEditOpusAgentMessageMutation();
  const abortRequestedRef = useRef<string | null>(null);
  const pollFailureCountsRef = useRef(new Map<string, number>());
  // The chat the known turns belong to — sends and the status-reconciliation
  // query are chat-scoped. Set on selection or adopted from the first send
  // of a new chat; null means "no chat exists yet".
  const chatIdRef = useRef<string | null>(null);
  // The first send of a new chat, while in flight: follow-up sends await it
  // so they land in the chat it creates instead of forking a second one.
  const newChatCreationRef = useRef<Promise<unknown> | null>(null);
  // RUNNING→terminal transitions reported by the status poll, held out of
  // rendered state until their settle (the final events fetch) lands: a turn
  // must never render as finished while its closing events may be missing.
  // A failed settle leaves the entry; the next status poll re-detects the
  // transition and settles again.
  const pendingTerminalRef = useRef(new Map<string, PolledStatus>());
  // In-flight settles by turn id, generation-stamped like poll batches: a
  // settle superseded as hung (or dropped by resumePolling) has its late
  // result ignored.
  const settlingRef = useRef(
    new Map<string, { generation: number; startedAt: number }>(),
  );
  const settleGenerationRef = useRef(0);
  // Consecutive status responses in which a tracked RUNNING turn was absent.
  const statusMissCountsRef = useRef(new Map<string, number>());

  // The chat's stored model as the client already knows it (its list item
  // or an earlier hydration normalized in the cache); undefined when the
  // chat is genuinely unknown, so the picker can show an indeterminate state
  // instead of naming a default the chat may not run on.
  const readCachedChatModel = useCallback(
    (chatId: string): OpusAgentModel | null | undefined => {
      try {
        const cached = client.readFragment<OpusAgentChatModelSeedFragment>({
          id: client.cache.identify({
            __typename: "OpusAgentChat",
            id: chatId,
          }),
          fragment: OpusAgentChatModelSeedFragmentDoc,
        });
        return cached ? cached.model ?? null : undefined;
      } catch {
        return undefined;
      }
    },
    [client],
  );

  // Mirror of a stored-model write into the normalized cache, so the
  // switch-time reseed (readCachedChatModel) reflects what the server holds.
  const writeCachedChatModel = useCallback(
    (chatId: string, model: OpusAgentModel | null) => {
      try {
        client.writeFragment<OpusAgentChatModelSeedFragment>({
          id: client.cache.identify({
            __typename: "OpusAgentChat",
            id: chatId,
          }),
          fragment: OpusAgentChatModelSeedFragmentDoc,
          data: { __typename: "OpusAgentChat", model },
        });
      } catch {
        // The cache seed is an optimization; the hook state is the truth.
      }
    },
    [client],
  );

  const resetToChat = useCallback(
    (chatId: string | null) => {
      sessionRef.current += 1;
      chatIdRef.current = chatId;
      selectedChatIdRef.current = chatId;
      setSelectedChatId(chatId);
      abortRequestedRef.current = null;
      newChatCreationRef.current = null;
      pollFailureCountsRef.current.clear();
      statusMissCountsRef.current.clear();
      // Seed synchronously so the trigger never names the default for a chat
      // that runs on a stored model while its hydration is still in flight; a
      // brand-new chat has no stored model (null = default). This is also
      // where a pick made before the initial lookup resolved migrates to
      // whatever chat became current. Precedence: the module's own stored
      // entry (kept across switches; every write to it is guarded) beats the
      // Apollo cache — a stale network-only result can write an old model
      // into the cache, so the cache seeds only chats the module has never
      // learned about.
      commitModelState(
        chatResolved(
          modelStateRef.current,
          chatId ?? NEW_CHAT_KEY,
          chatId
            ? chatId in modelStateRef.current.stored
              ? undefined
              : readCachedChatModel(chatId)
            : null,
          // A NEW composition starts from the device's sticky last pick;
          // existing chats never see it (their stored model is the truth).
          chatId
            ? null
            : useOpusAgentModelPreferenceStore.getState().preferredModel,
        ),
      );
      pendingTerminalRef.current.clear();
      settlingRef.current.clear();
      setTurns([]);
      // A failure banner always belongs to the selection that failed, never
      // to the one the user just switched to.
      setSelectionFailed(false);
      // A brand-new chat has no history to load.
      const alreadyHydrated = chatId === null;
      hydratedRef.current = alreadyHydrated;
      setHydrated(alreadyHydrated);
      persistSelection(chatId);
    },
    [commitModelState, readCachedChatModel, persistSelection],
  );

  // A restored selection that no longer exists (or is no longer readable):
  // forget it and let the most-recent-chat lookup pick the selection.
  const abandonRestoredSelection = useCallback(() => {
    restoredChatIdRef.current = null;
    useOpusAgentDrawerStore.getState().clearSelectedChat();
    sessionRef.current += 1;
    chatIdRef.current = null;
    selectedChatIdRef.current = undefined;
    setSelectedChatId(undefined);
    setSelectionAttempt((attempt) => attempt + 1);
  }, []);

  // Initial selection: the caller's most recent chat, or an empty new chat
  // when they have none. no-cache keeps this limit-1 probe out of the
  // OpusAgentChats cache entry the history list paginates.
  useEffect(() => {
    if (!activated || !active || selectedChatIdRef.current !== undefined)
      return;
    // A selection persisted for this user reopens directly, no lookup.
    const currentUserId = userIdRef.current;
    const restored =
      typeof currentUserId === "string"
        ? readPersistedSelectedChat(currentUserId)
        : null;
    if (restored) {
      restoredChatIdRef.current = restored;
      resetToChat(restored);
      return;
    }
    let cancelled = false;
    client
      .query<OpusAgentMostRecentChatQuery>({
        query: OpusAgentMostRecentChatDocument,
        fetchPolicy: "no-cache",
      })
      .then(({ data: recentData }) => {
        // A send or explicit selection that raced this lookup wins.
        if (cancelled || selectedChatIdRef.current !== undefined) return;
        resetToChat(recentData?.OpusAgentChats?.objects?.[0]?.id ?? null);
      })
      .catch(() => {
        if (cancelled || selectedChatIdRef.current !== undefined) return;
        setSelectionFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [activated, active, selectionAttempt, client, resetToChat]);

  const { data, loading, error, refetch } = useOpusAgentChatQuery({
    skip:
      !activated || !active || typeof selectedChatId !== "string" || hydrated,
    variables: { id: selectedChatId ?? "" },
    fetchPolicy: "network-only",
  });

  // Provenance for the hydration query: the session whose fetch was last
  // observed in flight. A result rendered without this selection's own fetch
  // (a leftover snapshot or null from a previous selection) is stale — it
  // must never hydrate the new chat as empty or banner it with an old error,
  // and an id-matched stale snapshot may only paint alongside an
  // authoritative refresh.
  const fetchSessionRef = useRef(-1);
  useEffect(() => {
    if (loading) {
      fetchSessionRef.current = sessionRef.current;
      fetchModelVersionRef.current = modelStateRef.current.version;
    }
  }, [loading, selectedChatId]);

  // The session whose settled fetch produced the currently exposed error;
  // tagged at settle time (loading just went false with an error present).
  const [errorSession, setErrorSession] = useState(-1);
  useEffect(() => {
    if (!loading && error) setErrorSession(fetchSessionRef.current);
  }, [loading, error]);

  // One reconciliation for a full server transcript (initial hydration and
  // the re-hydration after an edit). Superseded turns stay in state with
  // their stamp (hidden at render), terminal local state wins over a stale
  // RUNNING, locally accumulated events are kept (the merge is
  // index-idempotent), and a turn sent while the fetch was in flight stays
  // ahead of history — matched to its server twin when the send landed
  // before the fetch read.
  const applyServerTurns = useCallback(
    (serverTurns: OpusAgentTurnFragment[]) => {
      setTurns((prev) => {
        const prevIds = new Set(prev.map((turn) => turn.id));
        const matchedIds = new Set<string>();
        const reconciled = prev.map((turn) => {
          if (!turn.pending) return turn;
          const twin = serverTurns.find(
            (serverTurn) =>
              !prevIds.has(serverTurn.id) &&
              !matchedIds.has(serverTurn.id) &&
              serverTurn.status === OpusAgentChatTurnStatus.Running &&
              serverTurn.userMessage === turn.userMessage &&
              serverTurns.every(
                (other) =>
                  other.id === serverTurn.id ||
                  other.createdAt < serverTurn.createdAt,
              ),
          );
          if (!twin) return turn;
          matchedIds.add(twin.id);
          return mergeTurnFromServer(turn, twin);
        });
        const serverStates = serverTurns
          .filter((turn) => !matchedIds.has(turn.id))
          .map((serverTurn) => {
            const local = prev.find((turn) => turn.id === serverTurn.id);
            if (!local) return toTurnState(serverTurn);
            return mergeTurnFromServer(local, serverTurn);
          });
        const serverIds = new Set(serverStates.map((turn) => turn.id));
        // The server page is a bounded window (the last ~20 turns), so a
        // local turn missing from it is either a genuinely unresolved send
        // or history that AGED OUT of the window. Only the former may stay
        // (appended after history — a send result is never dropped):
        // optimistic entries, turns twin-matched from this very page, and
        // resolved turns strictly newer than the page's newest (the server
        // read predates them). Anything older follows hydration semantics
        // and drops from the drawer view — re-appending it would trail
        // ancient turns below the newest reply.
        const newestServerCreatedAt = serverTurns.reduce<string | null>(
          (newest, turn) =>
            newest === null || turn.createdAt > newest
              ? turn.createdAt
              : newest,
          null,
        );
        return [
          ...serverStates,
          ...reconciled.filter(
            (turn) =>
              !serverIds.has(turn.id) &&
              (turn.pending ||
                matchedIds.has(turn.id) ||
                newestServerCreatedAt === null ||
                turn.createdAt > newestServerCreatedAt),
          ),
        ];
      });
    },
    [],
  );

  // Authoritative full-transcript refresh of the current chat (used after an
  // edit rewound it, and to settle a hydration painted from a stale
  // snapshot); no-cache like the polls.
  const rehydrateChat = useCallback(async () => {
    const chatId = chatIdRef.current;
    if (!chatId) return;
    const modelVersion = modelStateRef.current.version;
    const { data: chatData } = await client.query<
      OpusAgentChatQuery,
      OpusAgentChatQueryVariables
    >({
      query: OpusAgentChatDocument,
      variables: { id: chatId },
      fetchPolicy: "no-cache",
    });
    if (chatIdRef.current !== chatId) return;
    if (chatData?.OpusAgentChat) {
      applyServerTurns(chatData.OpusAgentChat.turns);
      const model = chatData.OpusAgentChat.model ?? null;
      const applied = hydratedModelTransition(
        modelStateRef.current,
        chatId,
        model,
        modelVersion,
      );
      commitModelState(applied.state);
      // This fetch is no-cache: mirror an applied model so the reseed agrees.
      if (applied.applied) writeCachedChatModel(chatId, model);
    }
  }, [client, applyServerTurns, commitModelState, writeCachedChatModel]);

  useEffect(() => {
    if (hydratedRef.current || loading || !activated || !active) return;
    const chatId = selectedChatIdRef.current;
    if (typeof chatId !== "string") return;
    if (!data) return;
    const chat = data.OpusAgentChat;
    // A response for a previously selected chat must not hydrate this one.
    if (chat && chat.id !== chatId) return;
    // Data may only hydrate this selection when it provably came from this
    // selection's own fetch (its session saw the query in flight).
    const fresh = fetchSessionRef.current === sessionRef.current;
    // A null that predates this selection's fetch must never hydrate the
    // chat as empty; a FRESH null (deleted / unknown id) legitimately does.
    if (!chat && !fresh) return;
    if (restoredChatIdRef.current === chatId) {
      // The persisted selection is gone: fall back to the lookup instead of
      // showing a phantom empty chat.
      if (!chat) {
        abandonRestoredSelection();
        return;
      }
      restoredChatIdRef.current = null;
    }
    hydratedRef.current = true;
    setHydrated(true);
    if (chat && chat.turns.length > 0) applyServerTurns(chat.turns);
    // The model applies only from THIS selection's own fetch (`fresh`): a
    // re-served stale snapshot must never touch it — fetchModelVersionRef
    // belongs to the last fetch that went loading, not to a leftover result,
    // so the version guard alone cannot vouch for a !fresh snapshot. The
    // background rehydrate covers the !fresh case with its own captured
    // version. And a fetch that started before a send-success wrote the
    // stored model carries the pre-send value: turns still apply, the model
    // does not.
    if (chat && fresh) {
      commitModelState(
        hydratedModelTransition(
          modelStateRef.current,
          chat.id,
          chat.model ?? null,
          fetchModelVersionRef.current,
        ).state,
      );
    }
    if (chat && !fresh) {
      // An id-matched snapshot that predates this selection's fetch (e.g. a
      // quick A→B→A switch re-serving A's earlier result): paint it for an
      // instant transcript, but refresh authoritatively in the background —
      // hydrated=true tears down the in-flight network-only query.
      rehydrateChat().catch(() => undefined);
    }
  }, [
    data,
    loading,
    activated,
    active,
    selectedChatId,
    applyServerTurns,
    rehydrateChat,
    commitModelState,
    abandonRestoredSelection,
  ]);

  // A restored selection the server rejects (forbidden, unknown) falls back
  // the same way. Network trouble keeps the banner: the chat may well exist.
  useEffect(() => {
    if (loading || !error) return;
    const chatId = selectedChatIdRef.current;
    if (typeof chatId !== "string" || restoredChatIdRef.current !== chatId)
      return;
    if (!error.graphQLErrors?.length) return;
    abandonRestoredSelection();
  }, [loading, error, abandonRestoredSelection]);

  const abortTurnById = useCallback(
    (turnId: string) => {
      abortOpusAgentTurn({ variables: { turnId } })
        .then(({ data: abortData }) => {
          const result = abortData?.abortOpusAgentTurn;
          // A rejected abort changes nothing; the poll reflects the backend.
          if (!result?.success || !result.turn) return;
          const aborted = result.turn;
          // Terminal-wins: a late abort (the turn finished server-side just
          // before Stop landed) must not drag a settled turn back to RUNNING.
          setTurns((prev) =>
            prev.map((turn) =>
              turn.id === aborted.id
                ? { ...turn, ...mergeTurnStatus(turn, aborted) }
                : turn,
            ),
          );
        })
        .catch(() => {
          // Leave the turn RUNNING; the poll reflects whatever the backend did.
        });
    },
    [abortOpusAgentTurn],
  );

  // Refresh any mounted chats list (the history popover) so titles and
  // lastActivity track sends, renames arriving via polling, and the AI
  // titling pass; a no-op while no OpusAgentChats query is active.
  const refetchChatsList = useCallback(() => {
    client
      .refetchQueries({ include: ["OpusAgentChats"] })
      .catch(() => undefined);
  }, [client]);

  const applyPolledEvents = useCallback(
    (turnId: string, events: OpusAgentEventFragment[]) => {
      setTurns((prev) =>
        prev.map((t) => {
          if (t.id !== turnId) return t;
          return {
            ...t,
            events: mergeEvents(t.events, events),
            // A poll response arriving IS the connection working: clear the
            // lost-connection state so recovery is automatic (a batch
            // superseded at the failure ceiling can still succeed right
            // after the flag was set) and polling of this turn resumes.
            pollFailed: false,
          };
        }),
      );
    },
    [],
  );

  const applyPolledStatuses = useCallback((statuses: PolledStatus[]) => {
    if (statuses.length === 0) return [] as string[];
    const byId = new Map(statuses.map((polled) => [polled.id, polled]));
    // pollFailed belongs to the EVENTS pipeline: only a successful events
    // poll for THAT turn (or an explicit resumePolling) may clear it — a
    // healthy sibling's status response must not resurrect a tripped turn.
    //
    // A RUNNING→terminal transition is NOT applied here. The batch's
    // parallel events fetch may have read BEFORE the backend's finalize
    // flush, so the closing assistant text can still be missing (the old
    // single Poll query gave that atomicity for free; the events/status
    // split has to restore it explicitly). The terminal status is held and
    // lands together with the final events in settleTurn; until then the
    // turn keeps rendering as RUNNING — and, if the settle keeps failing,
    // as lost (banner + retry) rather than as a finished turn with a
    // truncated transcript. Detection repeats on every status poll until a
    // settle succeeds, which is what makes a failed settle retryable.
    const settleTerminalIds: string[] = [];
    for (const t of turnsRef.current) {
      const polled = byId.get(t.id);
      if (
        polled &&
        t.status === OpusAgentChatTurnStatus.Running &&
        polled.status !== OpusAgentChatTurnStatus.Running
      ) {
        pendingTerminalRef.current.set(t.id, polled);
        settleTerminalIds.push(t.id);
      }
    }
    setTurns((prev) =>
      prev.map((t) => {
        const polled = byId.get(t.id);
        if (!polled) return t;
        if (
          t.status === OpusAgentChatTurnStatus.Running &&
          polled.status !== OpusAgentChatTurnStatus.Running
        )
          return t;
        return {
          ...t,
          ...mergeTurnStatus(t, polled),
          citations:
            polled.status === OpusAgentChatTurnStatus.Running
              ? t.citations
              : polled.citations,
          // The supersede stamp is one-way: once set (locally by an edit or
          // by the server's rewind) it never clears.
          supersededByEditAt: t.supersededByEditAt ?? polled.supersededByEditAt,
        };
      }),
    );
    return settleTerminalIds;
  }, []);

  const recordPollFailure = useCallback((turnId: string) => {
    const failures = (pollFailureCountsRef.current.get(turnId) ?? 0) + 1;
    pollFailureCountsRef.current.set(turnId, failures);
    if (failures >= MAX_CONSECUTIVE_POLL_FAILURES) {
      setTurns((prev) =>
        prev.map((turn) =>
          turn.id === turnId ? { ...turn, pollFailed: true } : turn,
        ),
      );
    }
  }, []);

  // Straight to the lost-connection state: banner + Try again, polling of
  // this turn paused until resumePolling.
  const tripPolling = useCallback((turnId: string) => {
    pollFailureCountsRef.current.set(turnId, MAX_CONSECUTIVE_POLL_FAILURES);
    setTurns((prev) =>
      prev.map((turn) =>
        turn.id === turnId ? { ...turn, pollFailed: true } : turn,
      ),
    );
  }, []);

  // Every event of the turn past the locally known highest index, paged to
  // completion: an explicit limit per request, the cursor advanced to the
  // highest index received, until a short page (or no progress). Nothing is
  // handed back until the whole tail is in hand, so callers apply a complete
  // increment — never one page of it. `isCurrent` lets a superseded settle
  // abandon the loop between pages.
  const queryTurnEvents = useCallback(
    async (turnId: string, isCurrent: () => boolean = () => true) => {
      const turn = turnsRef.current.find((t) => t.id === turnId);
      let afterIndex = highestEventIndex(turn?.events ?? []);
      const collected: OpusAgentEventFragment[] = [];
      let sawList = false;
      for (;;) {
        const { data: pollData } = await client.query<
          OpusAgentTurnEventsQuery,
          OpusAgentTurnEventsQueryVariables
        >({
          query: OpusAgentTurnEventsDocument,
          variables: {
            input: {
              filters: { turnId, afterIndex },
              // Index order is the natural order; the explicit sort exists
              // so the server does not log its unsupported-default warning
              // on every 1s poll.
              sort: { column: "index", descending: false },
            },
            pagination: { limit: EVENTS_PAGE_SIZE },
          },
          fetchPolicy: "no-cache",
        });
        const page = pollData?.OpusAgentTurnEvents?.objects;
        if (!page) break;
        sawList = true;
        collected.push(...page);
        const next = highestEventIndex(page);
        if (
          page.length < EVENTS_PAGE_SIZE ||
          next <= afterIndex ||
          !isCurrent()
        )
          break;
        afterIndex = next;
      }
      return sawList ? collected : null;
    },
    [client],
  );

  const fetchTurnEvents = useCallback(
    async (turnId: string) => {
      const events = await queryTurnEvents(turnId);
      if (events && activeRef.current) applyPolledEvents(turnId, events);
    },
    [queryTurnEvents, applyPolledEvents],
  );

  // The settle for a held RUNNING→terminal transition: one final events
  // fetch, then events and terminal status land in ONE update. On failure
  // the turn stays RUNNING, the failure counts toward the pollFailed ceiling
  // (banner + Try again), and the next status poll — after resumePolling
  // restarts polling for a tripped turn — re-detects the transition and
  // settles again. At most one settle per turn is in flight, and a settled
  // turn is no longer locally RUNNING, so a success is exactly-once. For a
  // tripped turn the same settle doubles as the one-shot transcript
  // completion that lands the finished transcript without reopening
  // interval polling.
  //
  // Hang semantics mirror the poll batch: the settle's fetch runs outside
  // the batch's hang deadline, so a settle still unsettled past
  // POLL_HANG_TIMEOUT_MS when the next re-detection arrives is superseded
  // (a new generation; the hung request's late result is ignored) and
  // counted as a poll failure — otherwise a hung request would pin the turn
  // RUNNING forever after the server finished.
  const settleTurn = useCallback(
    async (turnId: string) => {
      const inFlight = settlingRef.current.get(turnId);
      if (inFlight) {
        if (Date.now() - inFlight.startedAt < POLL_HANG_TIMEOUT_MS) return;
        settlingRef.current.delete(turnId);
        recordPollFailure(turnId);
      }
      settleGenerationRef.current += 1;
      const generation = settleGenerationRef.current;
      settlingRef.current.set(turnId, { generation, startedAt: Date.now() });
      const session = sessionRef.current;
      const isCurrent = () =>
        settlingRef.current.get(turnId)?.generation === generation &&
        sessionRef.current === session;
      try {
        // Paged to completion under this settle's generation: a hang anywhere
        // in the loop is one superseded settle, a stale generation abandons
        // the loop, and the terminal status lands only after the last page.
        let events = (await queryTurnEvents(turnId, isCurrent)) ?? [];
        if (!isCurrent()) return;
        const terminal = pendingTerminalRef.current.get(turnId);
        if (terminal) {
          // The terminal status may only finalize a transcript that has its
          // closing event. Nothing new from the fetch while the transcript
          // still ends mid-work means the answer is likely still landing
          // (status read before the finalize flush): re-fetch with a short
          // backoff, the turn RUNNING meanwhile. The client cannot know the
          // final event index — a status payload carrying it would make this
          // exact (follow-up).
          const known = () =>
            turnsRef.current.find((t) => t.id === turnId)?.events ?? [];
          // The transcript as it would stand with this settle's increment.
          // Rows arriving is not the signal — the CLOSING event is: a partial
          // increment (one more tool or thinking row) mid-flush must not
          // finalize either.
          const closed = () =>
            transcriptLooksClosed(
              mergeEvents(known(), events),
              terminal.status,
            );
          for (const delay of SETTLE_REFETCH_DELAYS_MS) {
            if (closed()) break;
            await sleep(delay);
            if (!isCurrent()) return;
            const more = (await queryTurnEvents(turnId, isCurrent)) ?? [];
            if (!isCurrent()) return;
            events = mergeEvents(events, more);
          }
          if (!closed()) {
            // Still no closing event: never finalize a truncated transcript.
            // Paint whatever rows did arrive (they are real), and leave the
            // turn RUNNING behind the lost-connection affordance — Try again
            // (resumePolling) lets the next status poll re-detect the
            // transition (the held terminal stays for it) and settle afresh.
            if (events.length > 0) {
              const increment = events;
              setTurns((prev) =>
                prev.map((turn) =>
                  turn.id === turnId
                    ? { ...turn, events: mergeEvents(turn.events, increment) }
                    : turn,
                ),
              );
            }
            tripPolling(turnId);
            return;
          }
        }
        pendingTerminalRef.current.delete(turnId);
        pollFailureCountsRef.current.delete(turnId);
        setTurns((prev) =>
          prev.map((turn) => {
            if (turn.id !== turnId) return turn;
            const settled = {
              ...turn,
              events: mergeEvents(turn.events, events),
              pollFailed: false,
            };
            if (!terminal) return settled;
            return {
              ...settled,
              // Terminal-wins: an abort that landed meanwhile keeps its status.
              ...mergeTurnStatus(turn, terminal),
              citations:
                turn.status === OpusAgentChatTurnStatus.Running
                  ? terminal.citations
                  : turn.citations,
              supersededByEditAt:
                turn.supersededByEditAt ?? terminal.supersededByEditAt,
            };
          }),
        );
        // A turn going terminal moves the chat's lastActivity and (first
        // turn) triggers the AI titling pass: cheap-refresh the visible list.
        if (terminal) refetchChatsList();
      } catch {
        if (isCurrent()) recordPollFailure(turnId);
      } finally {
        if (settlingRef.current.get(turnId)?.generation === generation)
          settlingRef.current.delete(turnId);
      }
    },
    [queryTurnEvents, recordPollFailure, tripPolling, refetchChatsList],
  );

  const fetchTurnStatuses = useCallback(async () => {
    const chatId = chatIdRef.current;
    if (!chatId) return;
    // The response is only meaningful for the chat it was issued for: a hung
    // request the batch loop superseded can resolve after a chat switch, and
    // applied to the new chat it would count every live turn as absent (its
    // ids are not in the page) — a burst of them would abort them. Same
    // stale-session discipline as send and settle results.
    const session = sessionRef.current;
    // Every tracked turn must fit in the page: size it to reach the oldest
    // locally RUNNING server-side turn (rows from it to the end; optimistic
    // rows count as margin), never below the floor.
    const isTracked = (turn: OpusAgentTurnState) =>
      !turn.pending && turn.status === OpusAgentChatTurnStatus.Running;
    const oldestTracked = turnsRef.current.findIndex(isTracked);
    const rowsThroughOldestTracked =
      oldestTracked === -1 ? 0 : turnsRef.current.length - oldestTracked;
    const { data: pollData } = await client.query<
      OpusAgentTurnsQuery,
      OpusAgentTurnsQueryVariables
    >({
      query: OpusAgentTurnsDocument,
      variables: {
        input: {
          filters: { chatId },
          sort: { column: "createdAt", descending: true },
        },
        pagination: {
          limit: Math.max(STATUS_POLL_LIMIT, rowsThroughOldestTracked),
        },
      },
      fetchPolicy: "no-cache",
    });
    // A kill switch that landed mid-flight means no further requests and no
    // state effects: the settle this response would start is exactly that.
    if (!activeRef.current) return;
    if (sessionRef.current !== session) return;
    if (!pollData?.OpusAgentTurns) return;
    const statuses = pollData.OpusAgentTurns.objects;
    const settleTerminalIds = applyPolledStatuses(statuses);
    for (const turnId of settleTerminalIds) {
      settleTurn(turnId).catch(() => undefined);
    }
    // Terminal-by-absence (see MAX_STATUS_MISSES). A reported turn resets
    // its count; only a tracked turn missing from the response accrues one.
    const reported = new Set(statuses.map((polled) => polled.id));
    const absentIds: string[] = [];
    for (const turn of turnsRef.current) {
      if (!isTracked(turn)) continue;
      if (reported.has(turn.id)) {
        statusMissCountsRef.current.delete(turn.id);
        continue;
      }
      const misses = (statusMissCountsRef.current.get(turn.id) ?? 0) + 1;
      statusMissCountsRef.current.set(turn.id, misses);
      if (misses >= MAX_STATUS_MISSES) absentIds.push(turn.id);
    }
    if (absentIds.length === 0) return;
    for (const turnId of absentIds) statusMissCountsRef.current.delete(turnId);
    const completedAt = new Date().toISOString();
    setTurns((prev) =>
      prev.map((turn) =>
        absentIds.includes(turn.id) &&
        turn.status === OpusAgentChatTurnStatus.Running
          ? {
              ...turn,
              status: OpusAgentChatTurnStatus.Aborted,
              completedAt,
              pollFailed: false,
            }
          : turn,
      ),
    );
  }, [client, applyPolledStatuses, settleTurn]);

  const sendMessage = useCallback(
    (message: string) => {
      const trimmed = message.trim();
      if (!trimmed) return;
      // A send before the initial most-recent-chat lookup resolves starts a
      // NEW chat: the user's intent to talk beats resuming history.
      if (selectedChatIdRef.current === undefined) {
        selectedChatIdRef.current = null;
        setSelectedChatId(null);
        hydratedRef.current = true;
        setHydrated(true);
        // Any pick made while unresolved belongs to this new composition,
        // which otherwise starts from the device's sticky last pick.
        commitModelState(
          chatResolved(
            modelStateRef.current,
            NEW_CHAT_KEY,
            null,
            useOpusAgentModelPreferenceStore.getState().preferredModel,
          ),
        );
      }
      const session = sessionRef.current;
      const tempId = `pending-${uuid4()}`;
      setTurns((prev) => [
        ...prev,
        {
          id: tempId,
          userMessage: trimmed,
          status: OpusAgentChatTurnStatus.Running,
          createdAt: new Date().toISOString(),
          events: [],
          citations: [],
          pending: true,
        },
      ]);
      const markSendFailed = () => {
        if (sessionRef.current !== session) return;
        setTurns((prev) =>
          prev.map((turn) =>
            turn.id === tempId
              ? {
                  ...turn,
                  status: OpusAgentChatTurnStatus.Failed,
                  pending: false,
                  sendFailed: true,
                }
              : turn,
          ),
        );
        if (abortRequestedRef.current === tempId) {
          abortRequestedRef.current = null;
          const running = [...turnsRef.current]
            .reverse()
            .find(
              (turn) =>
                !turn.pending &&
                turn.status === OpusAgentChatTurnStatus.Running &&
                !turn.pollFailed,
            );
          if (running) abortTurnById(running.id);
        }
      };
      const liveContext = getLiveContext(router);
      // The chat's pending model pick rides on this send (the first send of a
      // new chat included) and, when set, becomes the chat's selection. The
      // snapshot is taken NOW, at send time — a send queued behind the chat's
      // creation must carry the pick the user saw when they hit send; a pick
      // changed while it waits belongs to the next turn. On success the pick
      // is consumed by identity under the chat the send landed in (the adopt
      // path moves the unsaved composition's entry, pickSeq included).
      const submitted = sendSubmitted(
        modelStateRef.current,
        chatIdRef.current ?? NEW_CHAT_KEY,
      );
      commitModelState(submitted.state);
      const { model, seq: modelSendSeq, pickSeq: carriedPickSeq } = submitted;
      const dispatch = async () => {
        if (chatIdRef.current === null && newChatCreationRef.current) {
          // Another send is creating this chat right now: wait for it so
          // this message lands there instead of forking a second chat.
          await newChatCreationRef.current.catch(() => undefined);
          if (sessionRef.current !== session) return;
        }
        const sendPromise = sendOpusAgentMessage({
          variables: {
            input: {
              message: trimmed,
              liveContext,
              chatId: chatIdRef.current,
              ...(model ? { model } : {}),
            },
          },
        });
        if (chatIdRef.current === null) {
          const creation = sendPromise.then(
            () => undefined,
            () => undefined,
          );
          newChatCreationRef.current = creation;
          creation.then(() => {
            if (newChatCreationRef.current === creation)
              newChatCreationRef.current = null;
          });
        }
        const { data: sendData } = await sendPromise;
        const result = sendData?.sendOpusAgentMessage;
        if (!result?.success || !result.turn) {
          markSendFailed();
          return;
        }
        const serverTurn = result.turn;
        // Applicability is about the CHAT, not the selection generation: the
        // result applies if it belongs to the chat selected right now (the
        // user may have left and come back — a return-visit hydration that
        // read before the server recorded it would otherwise leave the
        // message missing and unpolled), or to the new chat this very
        // composition is still creating (adopt it). A result for any other
        // chat is dropped; that chat's hydration shows it later.
        const composingSameNewChat =
          chatIdRef.current === null && sessionRef.current === session;
        if (!composingSameNewChat && chatIdRef.current !== serverTurn.chatId)
          return;
        // Where this send's pick lives now: still the unsaved composition if
        // this very send created the chat, else the chat it landed in.
        const modelKey = composingSameNewChat
          ? NEW_CHAT_KEY
          : chatIdRef.current ?? NEW_CHAT_KEY;
        if (composingSameNewChat) {
          // Adopt the chat this first message created.
          chatIdRef.current = serverTurn.chatId;
          selectedChatIdRef.current = serverTurn.chatId;
          setSelectedChatId(serverTurn.chatId);
          persistSelection(serverTurn.chatId);
        }
        setTurns((prev) => {
          // Merge onto the server row if a hydration already holds it (never
          // duplicate), else onto the optimistic row — which may have been
          // stamped superseded by an edit that landed meanwhile; the merge
          // keeps that stamp instead of reviving the branch as a live turn —
          // else append (the optimistic row was cleared by a chat switch).
          if (prev.some((turn) => turn.id === serverTurn.id))
            return prev
              .filter((turn) => turn.id !== tempId)
              .map((turn) =>
                turn.id === serverTurn.id
                  ? mergeTurnFromServer(turn, serverTurn)
                  : turn,
              );
          if (prev.some((turn) => turn.id === tempId))
            return prev.map((turn) =>
              turn.id === tempId ? mergeTurnFromServer(turn, serverTurn) : turn,
            );
          return [...prev, toTurnState(serverTurn)];
        });
        // Model bookkeeping for this send's success — compare-and-clear of
        // the pending pick, adopt migration, and the submit-order stored
        // write — all in one pure transition (see chatModelState.ts). A
        // FAILED send applies no transition at all: it neither consumes the
        // pending pick nor blocks an earlier success from writing. A stored
        // write is mirrored into the Apollo cache so the reseed on a later
        // switch-back agrees with the server.
        const adoptedChatId = composingSameNewChat ? serverTurn.chatId : null;
        const succeeded = sendSucceeded(
          modelStateRef.current,
          modelKey,
          model,
          modelSendSeq,
          carriedPickSeq,
          adoptedChatId,
        );
        commitModelState(succeeded.state);
        if (succeeded.storedWrite) {
          writeCachedChatModel(
            succeeded.storedWrite.chatId,
            succeeded.storedWrite.model,
          );
        }
        if (abortRequestedRef.current === tempId) {
          abortRequestedRef.current = null;
          abortTurnById(serverTurn.id);
        }
        if (!hydratedRef.current) {
          // A successful send proves the server is reachable again, so
          // refetch the history the initial load failed on - the
          // load-error banner clears through the normal hydration path
          // (and legitimately returns if this refetch fails too).
          refetch().catch(() => undefined);
        }
        refetchChatsList();
      };
      dispatch().catch(markSendFailed);
    },
    [
      router,
      sendOpusAgentMessage,
      abortTurnById,
      refetch,
      refetchChatsList,
      commitModelState,
      writeCachedChatModel,
      persistSelection,
    ],
  );

  const abort = useCallback(() => {
    const running = newestRunningTurn(turnsRef.current);
    if (!running) return;
    if (running.pending) {
      abortRequestedRef.current = running.id;
      return;
    }
    abortTurnById(running.id);
  }, [abortTurnById]);

  const resumePolling = useCallback((turnId: string) => {
    pollFailureCountsRef.current.delete(turnId);
    // A stuck settle must never block recovery: drop it (its late result is
    // ignored) so the restarted poll's re-detection settles afresh.
    settlingRef.current.delete(turnId);
    setTurns((prev) =>
      prev.map((turn) =>
        turn.id === turnId ? { ...turn, pollFailed: false } : turn,
      ),
    );
  }, []);

  const retryLoad = useCallback(() => {
    if (selectedChatIdRef.current === undefined) {
      setSelectionFailed(false);
      setSelectionAttempt((attempt) => attempt + 1);
      return;
    }
    refetch().catch(() => undefined);
  }, [refetch]);

  const selectChat = useCallback(
    (chatId: string) => {
      if (chatId === selectedChatIdRef.current) return;
      resetToChat(chatId);
    },
    [resetToChat],
  );

  const selectModel = useCallback(
    (model: OpusAgentModel) => {
      // The menu only offers available models; this is the guard beneath it.
      if (!isOpusAgentModelAvailable(model)) return;
      // Keyed to what the composer is looking at: a real chat, the unsaved
      // new chat, or — before the initial lookup resolves — the unresolved
      // sentinel, which follows whatever chat becomes current.
      const key = chatKeyFor(chatIdRef.current, selectedChatIdRef.current);
      commitModelState(
        selectModelTransition(modelStateRef.current, key, model),
      );
      // Every explicit pick becomes the device's sticky preference, seeding
      // future NEW chats (never overriding an existing chat's stored model).
      useOpusAgentModelPreferenceStore.getState().setPreferredModel(model);
    },
    [commitModelState],
  );

  const startNewChat = useCallback(() => {
    // Already composing an untouched new chat: nothing to clear.
    if (selectedChatIdRef.current === null && turnsRef.current.length === 0)
      return;
    resetToChat(null);
  }, [resetToChat]);

  const editMessage = useCallback(
    async (turnId: string, newMessage: string): Promise<boolean> => {
      const trimmed = newMessage.trim();
      if (!trimmed) return false;
      // The result belongs to the CHAT it edited, not to a selection
      // generation (same discipline as send results): a leave-and-return to
      // this chat bumps the session but must still accept the rewrite —
      // discarding it would pin a pre-rewind return hydration with no
      // RUNNING turn to poll it right, and polls never ADD the new turn.
      const chatId = chatIdRef.current;
      try {
        const { data: editData } = await editOpusAgentMessage({
          variables: { turnId, newMessage: trimmed },
        });
        const result = editData?.editOpusAgentMessage;
        if (!result?.success) return false;
        // success with turn === null: a concurrent send/edit held the chat's
        // lock and the rewrite was DROPPED. The edit did not apply, so the
        // caller keeps its editor (and draft) open; the server transcript is
        // still the truth, so re-hydrate either way.
        const applied = !!result.turn;
        // A different chat (or a new-chat composition) is selected now: this
        // chat's rewrite shows up on its own re-hydration later.
        if (chatIdRef.current !== chatId) return applied;
        const newTurn = result.turn;
        if (newTurn) {
          // Mirror the server's rewind locally: the edited turn and every
          // later one are superseded (hidden), the new turn goes live. A
          // return hydration may already hold post-rewind state (the edited
          // turn excluded, the new turn live): merge instead of duplicating.
          setTurns((prev) => {
            const editedIndex = prev.findIndex((turn) => turn.id === turnId);
            const rewound =
              editedIndex === -1
                ? prev
                : [
                    ...prev.slice(0, editedIndex),
                    ...prev.slice(editedIndex).map((turn) =>
                      turn.id === newTurn.id
                        ? turn
                        : {
                            ...turn,
                            status: OpusAgentChatTurnStatus.Aborted,
                            supersededByEditAt:
                              turn.supersededByEditAt ?? newTurn.createdAt,
                          },
                    ),
                  ];
            if (rewound.some((turn) => turn.id === newTurn.id))
              return rewound.map((turn) =>
                turn.id === newTurn.id
                  ? mergeTurnFromServer(turn, newTurn)
                  : turn,
              );
            return [...rewound, toTurnState(newTurn)];
          });
        }
        rehydrateChat().catch(() => undefined);
        refetchChatsList();
        return applied;
      } catch {
        return false;
      }
    },
    [editOpusAgentMessage, rehydrateChat, refetchChatsList],
  );

  const runningTurn = newestRunningTurn(turns);
  const hasPollableTurn = turns.some(isPollable);

  useEffect(() => {
    // `active` is a continuous gate: going inactive clears the interval (no
    // tick may fire); coming back starts a fresh one whose first tick
    // re-detects whatever finished meanwhile.
    if (!hasPollableTurn || !active) return;
    let cancelled = false;
    // A hung request must not freeze polling forever: the in-flight guard is
    // a generation-stamped batch with a hang deadline. A batch past the
    // deadline gets its turns failure-counted (so the pollFailed ceiling
    // still fires on dead networks) and is superseded; if its responses land
    // later they apply harmlessly (terminal-wins, index-idempotent merge)
    // but no longer control the guard.
    let batchId = 0;
    let batchActive = false;
    let batchStartedAt = 0;
    let batchTurnIds: string[] = [];
    const failureCounts = pollFailureCountsRef.current;
    const recordFailure = (turnId: string) => {
      if (!cancelled) recordPollFailure(turnId);
    };
    const tick = async () => {
      if (batchActive) {
        if (Date.now() - batchStartedAt < POLL_HANG_TIMEOUT_MS) return;
        batchTurnIds.forEach(recordFailure);
      }
      batchId += 1;
      const myBatch = batchId;
      batchActive = true;
      batchStartedAt = Date.now();
      const turnIds = turnsRef.current
        .filter(isPollable)
        .map((turn) => turn.id);
      batchTurnIds = turnIds;
      try {
        // One status-reconciliation fetch per batch (chat-scoped), shared by
        // every polled turn: a turn's tick succeeds only when both its
        // events increment AND the shared status fetch landed — terminal
        // transitions come exclusively from the status query now.
        const statusPromise = fetchTurnStatuses();
        await Promise.all(
          turnIds.map(async (turnId) => {
            try {
              await Promise.all([fetchTurnEvents(turnId), statusPromise]);
              failureCounts.delete(turnId);
            } catch {
              // Transient poll failure: keep polling until the failure ceiling.
              recordFailure(turnId);
            }
          }),
        );
      } finally {
        if (batchId === myBatch) {
          batchActive = false;
        }
      }
    };
    tick();
    const interval = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [
    hasPollableTurn,
    active,
    fetchTurnEvents,
    fetchTurnStatuses,
    recordPollFailure,
  ]);

  const isRunning = !!runningTurn;

  // An Apollo error only banners the selection whose own SETTLED fetch
  // produced it. Apollo keeps exposing the previous selection's error object
  // while the new fetch is in flight, so the error term is suppressed while
  // loading, and each settled error is tagged with the session of the fetch
  // that produced it (fetchSessionRef, stamped while that fetch was in
  // flight) — a leftover error from a chat the user switched away from
  // never banners the new one. selectionFailed is cleared on every switch
  // by resetToChat.
  const loadFailed =
    !hydrated &&
    ((!!error && !loading && errorSession === sessionRef.current) ||
      selectionFailed);

  // Superseded turns (an edit-and-resend rewound past them) are hidden from
  // the default transcript view. Known race window (accepted in review): a
  // normal send can land against a chat whose rewind hasn't been applied
  // yet, so the model briefly runs on the old branch — the treatment stays
  // simple regardless: hidden turns stay hidden, no special indicator.
  const visibleTurns = useMemo(
    () => turns.filter((turn) => !turn.supersededByEditAt),
    [turns],
  );

  const activeChatId =
    typeof selectedChatId === "string" ? selectedChatId : null;

  const { data: defaultModelData } = useOpusAgentDefaultModelQuery({
    skip: !activated || !active,
  });
  const defaultModel = defaultModelData?.OpusAgentDefaultModel ?? null;
  // What the NEXT turn will run on: the chat's pending pick, else its known
  // stored selection, else the server default — or indeterminate (null)
  // while the chat's stored model is genuinely unknown.
  const effectiveModel = effectiveChatModel(
    modelState,
    chatKeyFor(activeChatId, selectedChatId),
    defaultModel,
  );

  return useMemo(
    () => ({
      turns: visibleTurns,
      loading: !hydrated && !loadFailed,
      loadFailed,
      isRunning,
      activeChatId,
      effectiveModel,
      defaultModel,
      sendMessage,
      abort,
      resumePolling,
      retryLoad,
      selectChat,
      startNewChat,
      editMessage,
      selectModel,
    }),
    [
      visibleTurns,
      hydrated,
      loadFailed,
      isRunning,
      activeChatId,
      effectiveModel,
      defaultModel,
      sendMessage,
      abort,
      resumePolling,
      retryLoad,
      selectChat,
      startNewChat,
      editMessage,
      selectModel,
    ],
  );
};

gql`
  fragment OpusAgentEvent on OpusAgentChatTurnEvent {
    id
    index
    eventType
    createdAt
    payload {
      fallbackText
      assistantText {
        text
      }
      thinking {
        text
      }
      toolCall {
        callId
        toolName
        label
        status
        detail
      }
      planUpdate {
        done
        total
        activeStep
        items {
          text
          status
        }
      }
      error {
        message
      }
      contextCompacted {
        summaryChars
      }
    }
  }

  fragment OpusAgentCitationFields on OpusAgentCitation {
    type
    title
    quote
    libraryItemId
    url
  }

  fragment OpusAgentTurn on OpusAgentChatTurn {
    id
    chatId
    userMessage
    status
    model
    createdAt
    completedAt
    supersededByEditAt
    citations {
      ...OpusAgentCitationFields
    }
    events {
      ...OpusAgentEvent
    }
  }

  # Seeds the picker on a chat switch from whatever the client already holds
  # for that chat (its list item, an earlier hydration), read from the cache.
  fragment OpusAgentChatModelSeed on OpusAgentChat {
    model
  }

  query OpusAgentChat($id: UUID!) {
    OpusAgentChat(id: $id) {
      id
      createdAt
      model
      turns {
        ...OpusAgentTurn
      }
    }
  }

  # The server default, so the picker can name the entry "default" resolves to.
  query OpusAgentDefaultModel {
    OpusAgentDefaultModel
  }

  query OpusAgentMostRecentChat {
    OpusAgentChats(input: {}, pagination: { limit: 1, offset: 0 }) {
      objects {
        id
      }
    }
  }

  query OpusAgentTurns(
    $input: OpusAgentTurnsInput!
    $pagination: PaginationInput
  ) {
    OpusAgentTurns(input: $input, pagination: $pagination) {
      objects {
        id
        status
        completedAt
        supersededByEditAt
        citations {
          ...OpusAgentCitationFields
        }
      }
    }
  }

  query OpusAgentTurnEvents(
    $input: OpusAgentTurnEventsInput!
    $pagination: PaginationInput
  ) {
    OpusAgentTurnEvents(input: $input, pagination: $pagination) {
      objects {
        ...OpusAgentEvent
      }
    }
  }

  mutation SendOpusAgentMessage($input: SendOpusAgentMessageInput!) {
    sendOpusAgentMessage(input: $input) {
      success
      error {
        code
        message
      }
      turn {
        ...OpusAgentTurn
      }
    }
  }

  mutation AbortOpusAgentTurn($turnId: UUID!) {
    abortOpusAgentTurn(turnId: $turnId) {
      success
      error {
        code
        message
      }
      turn {
        id
        status
        completedAt
      }
    }
  }

  mutation EditOpusAgentMessage($turnId: UUID!, $newMessage: String!) {
    editOpusAgentMessage(turnId: $turnId, newMessage: $newMessage) {
      success
      error {
        code
        message
      }
      turn {
        ...OpusAgentTurn
      }
    }
  }
`;
